<script setup lang="ts">
import type { EsProduct } from '~/types/elastic'

const props = withDefaults(defineProps<{
  product: EsProduct
}>(), {
})

const { product } = toRefs(props)

const { pending } = useCart()
const { addToCart } = useAddToCart()
const adding = ref(false)
const added = ref(false)

const prices = computed(() => {
  if (product.value?.fromPrice) {
    return {
      regularPrice: product.value.fromPrice,
      finalPrice: product.value.fromPrice,
    }
  }
  return {
    regularPrice: product.value.regularPrice,
    finalPrice: product.value.finalPrice,
  }
})

const add = async () => {
  const item = [{
    sku: product.value?.sku || '',
    quantity: 1,
  }]

  if (product.value?.type !== 'simple') {
    navigateTo(product.value?.url || '/')
    adding.value = false
    added.value = false
  }
  else {
    adding.value = true
    try {
      await addToCart(item, {
        suppress: true,
        product: product.value,
      })
      added.value = true
    }
    catch (err) {
      console.error(err)
      added.value = false
    }
    finally {
      adding.value = false
    }
  }
}
</script>

<template>
  <div>
    <AppLink
      :to="product?.url"
      class="product-info__visual"
    >
      <img
        :src="useXcdnImage(product?.image, 'f=rs:fit:100:115', product?.name)"
        :alt="product?.name"
      >
    </AppLink>
    <div class="product-info__content">
      <span class="product-info__name">
        {{ product?.name }}
      </span>
      <span
        v-if="product?.brand"
        class="product-info__brand"
      >
        {{ product.brand?.label }}
      </span>
      <UiPrice
        v-if="prices"
        class="product-item__price"
        :prices="prices"
      >
        <template
          v-if="product?.fromPrice"
          #prefix
        >
          v.a.
        </template>
      </UiPrice>
    </div>
    <div class="product-info__actions">
      <button
        type="button"
        class="button product-info__action-add-to-cart"
        :class="{
          'button--loading': adding,
          'button--added': added,
        }"
        :disabled="pending"
        @click="add"
      >
        <template v-if="adding">
          <Icon
            name="icon:loader"
            class="icon icon--loader"
          />
        </template>
        <template v-else-if="added">
          <Icon
            name="icon:check"
            class="icon icon--check"
          />
        </template>
        <template v-else>
          <Icon
            :name="product?.type === 'simple' ? 'icon:atc' : 'icon:arrow'"
            class="icon icon--atc"
          />
          {{ $t(product?.type === 'simple' ? 'Add to cart' : 'View product') }}
        </template>
      </button>
    </div>
  </div>
</template>
